import React, { useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "http://nissi-india.com/api";

const ItemCategoryMas = ({ onSuccess, onCancel, editData }) => {
  const [itemCategory, setItemCategory] = useState("");
  const [itemCategoryCode, setItemCategoryCode] = useState("");
  const [remarks, setRemarks] = useState("");
const [isReturnable, setIsReturnable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");

  const { skin, isDark } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor } = currentSkin;

  // LocalStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // Common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  useEffect(() => {
    setItemCategory(editData?.fld_itemcategory_name || "");
    setItemCategoryCode(editData?.fld_itemCategoryCode || "");
    setRemarks(editData?.fld_remarks || "");
    setIsReturnable(
      editData?.fld_isReturnable === 1 ||
        editData?.fld_isReturnable === true ||
        false
    );

    if (!editData) {
      generateNextCategoryCode();
    }
  }, [editData]);

  const generateNextCategoryCode = async () => {
    try {
      const response = await axios.get(`${API_BASE}/itemList_catCode`, {
        headers: commonHeaders,
      });

      console.log("🔹 Auto-generated category code response:", response.data);

      if (response.data?.nextItemCategoryCode) {
        setItemCategoryCode(response.data.nextItemCategoryCode.toString());
      } else {
        toast.error("Invalid response from server for category code.");
        setItemCategoryCode("");
      }
    } catch (error) {
      console.error("Error fetching next category code:", error);
      toast.error("Failed to get auto-generated category code.");
      setItemCategoryCode("");
    }
  };

  const resetForm = async () => {
    setItemCategory("");
    setRemarks("");
    setIsReturnable(false);
    await generateNextCategoryCode();
  };

const handleSubmit = async (addAnother = false) => {
    setFormError("");

    if (!itemCategory || itemCategory.trim() === "") {
      setFormError("Item Category cannot be empty!");
      return;
    }

    if (!itemCategoryCode) {
      setFormError("Item Category Code is missing!");
      return;
    }

    if (!userId) {
      setFormError("User ID not found in localStorage.");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: itemCategory.trim(),
        code: itemCategoryCode,
        remarks: remarks || "",
        userId: Number(userId),
        isReturnable: isReturnable ? 1 : 0,
      };

      if (editData?.fld_itemcategoryid) {
        payload.id = editData.fld_itemcategoryid;
      }

      console.log("🚨 Submitting payload:", payload);
      console.log("🚨 Headers:", commonHeaders);

   if (editData?.fld_itemcategoryid) {
        await axios.put(
          `${API_BASE}/itemCategory_edit/${payload.id}`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              ...commonHeaders,
            },
          }
        );
      } else {
        await axios.post(`${API_BASE}/itemCategory_insert`, payload, {
          headers: {
            "Content-Type": "application/json",
            ...commonHeaders,
          },
        });
      }

  if (addAnother && !editData) {
        setFormError("");
        toast.success("Category saved! Add the next one.");
        await resetForm();
      } else {
        setFormError("");
        onSuccess?.();
      }
} catch (error) {
      console.error("Error saving item category:", error);

      if (error.response) {
        console.error("🔴 Server response error:", error.response.data);
        const serverMsg =
          error.response.data?.message || error.response.data?.error;

        if (
          error.response.status === 400 &&
          serverMsg?.toLowerCase().includes("already exists")
        ) {
          setFormError("Item already exists!");
        } else {
          setFormError(serverMsg || "Unexpected error occurred.");
        }
      } else {
        setFormError("Error saving item category.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative font-inter w-full">
            <div
        className={`w-full relative overflow-hidden rounded-2xl shadow-xl transition-all duration-300 border ${
          isDark
            ? `${currentSkin.dark} border-slate-700/50`
            : `bg-gray-300 border-slate-200/60`
        }`}
      >
      <div
          className={`relative px-6 py-4 border-b ${
            isDark
              ? "border-slate-700/50 bg-slate-900/60"
              : "border-slate-200/50 bg-white/60"
          } backdrop-blur-md`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md shadow-${baseColor}-500/25`}
              >
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.99 1.99 0 013 12V7a4 4 0 014-4z"
                  />
                </svg>
              </div>

              <div>
                <h3
                  className={`text-lg font-bold tracking-tight ${
                    isDark ? "text-white" : "text-slate-900"
                  }`}
                >
                  {editData ? "Edit Category" : "Create New Category"}
                </h3>
                <p
                  className={`text-xs font-medium ${
                    isDark ? "text-slate-400" : "text-slate-600"
                  }`}
                >
                  {editData
                    ? "Update your category information"
                    : "Add a new category to your inventory"}
                </p>
              </div>
            </div>

            <button
              onClick={onCancel}
              className={`group relative p-2 rounded-lg transition-all duration-300 hover:rotate-90 ${
                isDark
                  ? "text-slate-400 hover:text-white hover:bg-slate-800/80 border border-slate-700/50 hover:border-slate-600"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100/80 border border-slate-200/50 hover:border-slate-300"
              }`}
              aria-label="Close form"
            >
              <svg
                className="w-4 h-4 transition-transform duration-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

<div className="relative p-6">
          {formError && (
            <div
              className={`flex items-center justify-between gap-3 mb-4 px-4 py-2.5 rounded-lg border text-sm font-medium ${
                isDark
                  ? "bg-red-900/30 border-red-500/40 text-red-300"
                  : "bg-red-50 border-red-200 text-red-700"
              }`}
            >
              <span>{formError}</span>
              <button
                type="button"
                onClick={() => setFormError("")}
                className="shrink-0 opacity-70 hover:opacity-100"
                aria-label="Dismiss error"
              >
                ✕
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="space-y-1.5">
              <label
                className={`flex items-center gap-1.5 text-xs font-semibold tracking-wide ${
                  isDark ? "text-slate-300" : "text-slate-700"
                }`}
              >
                Category Name
                <span className="text-red-500 font-bold">*</span>
              </label>

              <div className="relative">
                <input
                  type="text"
                  value={itemCategory}
                  onChange={(e) => setItemCategory(e.target.value)}
                  maxLength={50}
                  className={`w-full px-3 py-2 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
                    isDark
                      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
                      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
                  }`}
                  placeholder="Enter category name"
                  required
                />
                <div
                  className={`absolute -bottom-4 right-0 text-[10px] font-medium ${
                    isDark ? "text-slate-500" : "text-slate-400"
                  }`}
                >
                  {itemCategory.length}/50
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <label
                className={`flex items-center gap-1.5 text-xs font-semibold tracking-wide ${
                  isDark ? "text-slate-300" : "text-slate-700"
                }`}
              >
                Category Code
                <span className="text-red-500 font-bold">*</span>
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase border ${
                    isDark
                      ? "bg-emerald-900/30 text-emerald-400 border-emerald-500/30"
                      : "bg-emerald-100 text-emerald-700 border-emerald-200"
                  }`}
                >
                  Auto
                </span>
              </label>

              <input
                type="text"
                value={itemCategoryCode}
                disabled
                className={`w-full px-3 py-2 rounded-lg border text-sm font-semibold tracking-wide cursor-not-allowed ${
                  isDark
                    ? "bg-slate-800/40 border-slate-600/30 text-slate-300"
                    : "bg-slate-100 border-slate-300 text-slate-600"
                }`}
                placeholder="Auto-generated"
              />
            </div>

            <div className="xl:col-span-2 space-y-1.5">
              <label
                className={`text-xs font-semibold tracking-wide ${
                  isDark ? "text-slate-300" : "text-slate-700"
                }`}
              >
                Remarks
              </label>

              <div className="relative">
                <textarea
  value={remarks}
  onChange={(e) => setRemarks(e.target.value)}
  className={`w-full px-3 py-2 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-500 resize-none ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`}
  placeholder="Optional notes about this category..."
  rows={2}
  maxLength={150}
/>
<div
  className={`absolute -bottom-4 right-0 text-[10px] font-medium ${
    isDark ? "text-slate-500" : "text-slate-400"
  }`}
>
  {remarks.length}/150
</div>
              </div>
            </div>

            <div className="xl:col-span-2">
              <label
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  isDark
                    ? "border-slate-600/50 bg-slate-800/30 hover:bg-slate-800/50"
                    : "border-slate-300/60 bg-slate-50/50 hover:bg-slate-100/50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={isReturnable}
                  onChange={(e) => setIsReturnable(e.target.checked)}
                  className={`w-4 h-4 rounded border-2 accent-${baseColor}-500`}
                />

                <div>
                  <span
                    className={`text-sm font-semibold ${
                      isDark ? "text-white" : "text-slate-900"
                    }`}
                  >
                    Is Returnable Item
                  </span>
                  <p
                    className={`text-xs mt-0.5 ${
                      isDark ? "text-slate-400" : "text-slate-600"
                    }`}
                  >
                    Items in this category can be returned after purchase
                  </p>
                </div>
              </label>
            </div>
          </div>

          <div className="flex flex-wrap justify-end gap-3 mt-6 pt-4 border-t border-slate-200/60 dark:border-slate-700/60">
            <button
              onClick={onCancel}
              type="button"
              className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                isDark
                  ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                  : "border-slate-300 text-slate-700 hover:bg-slate-100"
              }`}
            >
              Cancel
            </button>

            {/* {!editData && (
              <button
                onClick={() => handleSubmit(true)}
                disabled={loading}
                className={`px-4 py-2 text-sm font-semibold text-white rounded-lg shadow transition-colors ${
                  loading
                    ? "opacity-50 cursor-not-allowed bg-slate-400"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                Save & Add New
              </button>
            )} */}

         <button
              onClick={() => handleSubmit(false)}
              disabled={loading}
              className={`px-5 py-2 text-sm font-semibold text-white rounded-lg shadow transition-colors ${
                loading
                  ? "opacity-50 cursor-not-allowed bg-slate-400"
                  : `bg-${baseColor}-600 hover:bg-${baseColor}-700`
              }`}
            >
              {loading ? "Saving..." : editData ? "Update Category" : "Save Category"}
            </button>
          </div>
        </div>
      </div>
     </div>
  );
};

export default ItemCategoryMas;